import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/stores/auth';
import { useCrypto } from '../lib/stores/crypto';
import { deriveEncryptionKey, encrypt, decrypt } from '@mysecureroom/crypto';
import { base64ToBytes, bytesToBase64 } from '@mysecureroom/crypto';
import { api } from '../lib/api/client';
import { Button, Card, CardHeader, CardBody, Input } from '../lib/design-system/components';
import './Unlock.css';

export default function Unlock() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setEncryptionKey } = useCrypto();
  const [passphrase, setPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [wordCount, setWordCount] = useState(4);
  const [generatedPassphrase, setGeneratedPassphrase] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);
  const [copied, setCopied] = useState(false);

  // Word list for passphrase generation (EFF long wordlist subset)
  const wordList = [
    'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
    'account', 'achieve', 'acid', 'acquire', 'across', 'action', 'actor', 'actress',
    'actual', 'adapt', 'address', 'adjust', 'admiral', 'advance', 'advice', 'aerobic',
    'afford', 'afraid', 'again', 'agent', 'agree', 'ahead', 'airport', 'aisle',
    'alarm', 'album', 'alcohol', 'alert', 'alien', 'alive', 'almost', 'alone',
    'alpha', 'already', 'also', 'alter', 'always', 'amateur', 'amazing', 'among',
    'amount', 'amused', 'analyst', 'anchor', 'ancient', 'anger', 'angle', 'angry',
    'animal', 'ankle', 'announce', 'annual', 'another', 'answer', 'antenna', 'antique',
    'anxiety', 'apart', 'apology', 'appear', 'apple', 'approve', 'april', 'arch',
    'arctic', 'area', 'arena', 'argue', 'armed', 'armor', 'army', 'around',
    'arrange', 'arrest', 'arrive', 'arrow', 'artist', 'artwork', 'aside', 'assault',
    'asset', 'assist', 'assume', 'asthma', 'athlete', 'atom', 'attack', 'attend',
    'attitude', 'attract', 'auction', 'audit', 'august', 'aunt', 'author', 'auto',
    'autumn', 'average', 'avocado', 'avoid', 'awake', 'aware', 'away', 'awesome',
    'awful', 'awkward', 'axis', 'baby', 'bachelor', 'bacon', 'badge', 'balance',
    'balcony', 'ball', 'bamboo', 'banana', 'banner', 'barrel', 'base', 'basic',
    'basket', 'battle', 'beach', 'beauty', 'because', 'become', 'beef', 'before',
    'begin', 'behave', 'behind', 'believe', 'below', 'bench', 'benefit', 'best',
    'betray', 'better', 'between', 'beyond', 'bicycle', 'billion', 'biology', 'bird',
    'birth', 'bitter', 'black', 'blade', 'blame', 'blanket', 'blast', 'bleak'
  ];

  const generatePassphrase = () => {
    const words: string[] = [];
    for (let i = 0; i < wordCount; i++) {
      const randomIndex = Math.floor(Math.random() * wordList.length);
      words.push(wordList[randomIndex]);
    }
    const phrase = words.join('-');
    setGeneratedPassphrase(phrase);
    setCopied(false);
  };

  const copyToClipboard = async () => {
    if (!generatedPassphrase) return;
    try {
      await navigator.clipboard.writeText(generatedPassphrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const populatePassphrase = () => {
    if (!generatedPassphrase) return;
    setPassphrase(generatedPassphrase);
    if (isSetup) {
      setConfirmPassphrase(generatedPassphrase);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Check if encryption is already set up
    checkEncryptionSetup();
  }, [user]);

  const checkEncryptionSetup = async () => {
    try {
      await api.get('/user/encryption/salt');
      setIsSetup(false); // Salt exists, proceed to unlock
    } catch (err) {
      setIsSetup(true); // No salt, need to set up encryption
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passphrase !== confirmPassphrase) {
      setError('Passphrases do not match');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Generate random salt for encryption key derivation
      const salt = crypto.getRandomValues(new Uint8Array(16));

      // Derive encryption key
      const encryptionKey = await deriveEncryptionKey(passphrase, salt);

      // Create verification blob
      const verificationPlaintext = 'mysecureroom-key-verification';
      const verificationBlob = encrypt(encryptionKey, verificationPlaintext);

      // Save salt and verification to server
      await api.post('/user/encryption/setup', {
        salt: bytesToBase64(salt),
        verification_blob: verificationBlob,
      });

      // Set key in crypto store
      setEncryptionKey(encryptionKey);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Failed to set up encryption');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Fetch salt from server
      const { salt: saltB64 } = await api.get<{ salt: string }>('/user/encryption/salt');
      const salt = base64ToBytes(saltB64);

      // Re-derive encryption key
      const encryptionKey = await deriveEncryptionKey(passphrase, salt);

      // Verify key by decrypting verification blob
      const { verification_blob } = await api.get<{ verification_blob: string }>('/user/encryption/verification');
      const decrypted = decrypt(encryptionKey, verification_blob);

      if (decrypted !== 'mysecureroom-key-verification') {
        throw new Error('Invalid passphrase');
      }

      // Set key in crypto store
      setEncryptionKey(encryptionKey);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Invalid passphrase');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSetup) {
    return (
      <div className="unlock-container">
        <div className="unlock-content">
          <div className="unlock-header">
            <h1 className="heading-1">🔐 Set Up Encryption</h1>
            <p className="text-large text-secondary">
              Choose a passphrase to encrypt your data. This passphrase cannot be recovered if forgotten.
            </p>
          </div>

          <Card elevation="lg" padding="lg">
            <CardBody>
              <form onSubmit={handleSetup} className="unlock-form">
                <div className="passphrase-field">
                  <Input
                    label="Encryption Passphrase"
                    type={showPassphrase ? 'text' : 'password'}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    required
                    fullWidth
                    placeholder="Choose a strong passphrase"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassphrase(!showPassphrase)}
                  >
                    {showPassphrase ? '🙈' : '👁️'}
                  </button>
                </div>

                <div className="passphrase-generator">
                  <button
                    type="button"
                    className="generator-toggle"
                    onClick={() => setShowGenerator(!showGenerator)}
                  >
                    {showGenerator ? '▼' : '▶'} Passphrase Suggestion Tool
                  </button>

                  {showGenerator && (
                    <div className="generator-content">
                      <div className="generator-controls">
                        <label className="text-small">
                          Number of words: <strong>{wordCount}</strong>
                        </label>
                        <input
                          type="range"
                          min="3"
                          max="5"
                          value={wordCount}
                          onChange={(e) => setWordCount(parseInt(e.target.value))}
                          className="word-slider"
                        />
                        <div className="slider-labels">
                          <span className="text-small text-secondary">3 words</span>
                          <span className="text-small text-secondary">5 words</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        fullWidth
                        onClick={generatePassphrase}
                      >
                        🎲 Generate Random Passphrase
                      </Button>

                      {generatedPassphrase && (
                        <div className="generated-passphrase">
                          <div className="passphrase-display">
                            <code className="text-base">{generatedPassphrase}</code>
                          </div>
                          <div className="passphrase-actions">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={copyToClipboard}
                            >
                              {copied ? '✓ Copied!' : '📋 Copy'}
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={populatePassphrase}
                            >
                              ↓ Use This Passphrase
                            </Button>
                          </div>
                          <p className="text-small text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                            🔒 <strong>Save this passphrase somewhere safe!</strong> Write it down or use a password manager. You cannot recover your data without it.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Input
                  label="Confirm Passphrase"
                  type={showPassphrase ? 'text' : 'password'}
                  value={confirmPassphrase}
                  onChange={(e) => setConfirmPassphrase(e.target.value)}
                  required
                  fullWidth
                  placeholder="Confirm your passphrase"
                  error={error}
                />

                <div className="info-box">
                  <p className="text-small">
                    ⚠️ <strong>Important:</strong> Your passphrase encrypts all your data. 
                    If you forget it, your data cannot be recovered. Store it securely!
                  </p>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={isLoading}
                >
                  Enable Encryption
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="unlock-container">
      <div className="unlock-content">
        <div className="unlock-header">
          <h1 className="heading-1">🔓 Unlock Your Data</h1>
          <p className="text-large text-secondary">
            Enter your encryption passphrase to decrypt your data.
          </p>
        </div>

        <Card elevation="lg" padding="lg">
          <CardBody>
            <form onSubmit={handleUnlock} className="unlock-form">
              <div className="passphrase-field">
                <Input
                  label="Encryption Passphrase"
                  type={showPassphrase ? 'text' : 'password'}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  required
                  fullWidth
                  placeholder="Enter your passphrase"
                  error={error}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                >
                  {showPassphrase ? '🙈' : '👁️'}
                </button>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isLoading}
              >
                Unlock
              </Button>
            </form>
          </CardBody>
        </Card>

        <p className="text-small text-tertiary unlock-footer">
          Logged in as: <strong>{user?.email}</strong>
        </p>
      </div>
    </div>
  );
}
